import React from 'react';
import { Filter } from 'lucide-react';
import { cn } from '../../utils/cn';
import ColumnFilterPopover, { type ColumnFilterItem } from './ColumnFilterPopover';

interface ColumnFilterButtonProps<V extends string = string> {
  /** Dropdown rows. */
  items: ColumnFilterItem<V>[];
  /** Currently-selected values. Empty set means "no filter". */
  selected: ReadonlySet<V>;
  /** Replace the selected set (not diff — caller decides). */
  onChange: (next: Set<V>) => void;
  /** Copy shown at the top of the popover (e.g. "Filter by basket"). */
  title?: string;
  /** Force the inner search box on/off — default auto (>7 items). */
  searchable?: boolean;
  /** Tooltip on the icon. */
  ariaLabel: string;
}

/**
 * Inline header-cell filter trigger. Renders a small funnel icon that opens
 * the multi-select popover (portalled to document.body). Doesn't interfere
 * with column drag or sort — `onMouseDown` / `onDragStart` are stopped at
 * the button boundary.
 *
 * Visual states:
 *   • inactive → muted grey funnel, hover darkens
 *   • active (selection present) → solid blue funnel + tiny dot badge
 *   • open → subtle ring + soft fill to show popover ownership
 */
export function ColumnFilterButton<V extends string = string>({
  items, selected, onChange, title, searchable, ariaLabel,
}: ColumnFilterButtonProps<V>) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const isActive = selected.size > 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        draggable={false}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          'relative inline-flex items-center justify-center w-5 h-5 rounded transition-colors',
          isActive
            ? 'text-blue-500 dark:text-blue-400'
            : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200',
          open && 'ring-1 ring-blue-400/40 bg-blue-50/60 dark:bg-blue-900/20',
        )}
        title={ariaLabel}
      >
        <Filter className="w-3 h-3" strokeWidth={isActive ? 2.5 : 2} />
        {isActive && (
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-blue-500 ring-2 ring-white dark:ring-gray-700" />
        )}
      </button>
      {open && triggerRef.current && (
        <ColumnFilterPopover
          items={items}
          selected={selected}
          onChange={onChange}
          anchor={triggerRef.current}
          title={title}
          searchable={searchable}
          ariaLabel={ariaLabel}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default ColumnFilterButton;
